import { ChangeDetectionStrategy, Component, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { UI } from '../ui';
import { ColorFieldComponent } from './color-field.component';
import {
  DEFAULT_LOCALES,
  type CheckoutAppearanceValue,
  type Option,
} from './merchant-config.models';

/** Checkout brand colours, default language and optional custom CSS. */
@Component({
  selector: 'mm-checkout-appearance-config',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ColorFieldComponent, ...UI],
  template: `
    <div class="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
      @for (field of colorFields; track field.key) {
        <mm-color-field
          [label]="field.label"
          [disabled]="disabled()"
          [value]="value()[field.key]"
          (valueChange)="set(field.key, $event)"
        />
      }
      <label class="flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Default language</span>
        <select
          uiInput
          [ngModel]="value().checkout_default_locale"
          (ngModelChange)="set('checkout_default_locale', $event)"
          [disabled]="disabled()"
        >
          @for (locale of locales(); track locale.value) {
            <option [value]="locale.value">{{ locale.label }}</option>
          }
        </select>
      </label>
    </div>
    @if (showCss()) {
      <label class="mt-4 flex flex-col gap-1 text-[0.8125rem] font-semibold text-fg">
        <span>Custom CSS (optional)</span>
        <textarea
          uiInput
          rows="4"
          class="font-mono"
          placeholder=".pl-button { border-radius: 8px; }"
          [ngModel]="value().checkout_custom_css"
          (ngModelChange)="set('checkout_custom_css', $event)"
          [disabled]="disabled()"
        ></textarea>
        <span class="text-xs font-normal text-muted">Only the published .pl-* classes are accepted.</span>
      </label>
    }
  `,
})
export class CheckoutAppearanceConfigComponent {
  readonly value = model.required<CheckoutAppearanceValue>();
  readonly disabled = input(false);
  readonly showCss = input(true);
  readonly locales = input<readonly Option[]>(DEFAULT_LOCALES);

  readonly colorFields = [
    { key: 'checkout_primary_color', label: 'Primary color' },
    { key: 'checkout_secondary_color', label: 'Secondary color' },
    { key: 'checkout_accent_color', label: 'Accent color' },
    { key: 'checkout_background_color', label: 'Background color' },
  ] as const;

  set<K extends keyof CheckoutAppearanceValue>(key: K, next: CheckoutAppearanceValue[K]): void {
    this.value.update((current) => ({ ...current, [key]: next }));
  }
}
